/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AttributeEntity } from '../models/AttributeEntity';
import type { AttributeEntityParams } from '../models/AttributeEntityParams';
import type { AttributeEntityValueTypes } from '../models/AttributeEntityValueTypes';
import type { AttributeType } from '../models/AttributeType';
import type { AttributeTypeWithCounts } from '../models/AttributeTypeWithCounts';
import type { AttributeTypeWithValues } from '../models/AttributeTypeWithValues';
import type { AttributeUsageCounts } from '../models/AttributeUsageCounts';
import type { AttributeValue } from '../models/AttributeValue';
import type { CustomerAttribute } from '../models/CustomerAttribute';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class CustomerAttributesService {

    /**
     * Gets attribute types grouped by entity type
     * Get attribute types grouped by entity type
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeEntities(): CancelablePromise<{
        LOCATION?: Array<AttributeEntity>;
        LOCATIONGROUP?: Array<AttributeEntity>;
        OPENING?: Array<AttributeEntity>;
        POSITION?: Array<AttributeEntity>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_entities',
        });
    }

    /**
     * Search one attribute type's values
     * Substring search over one attribute type's values, capped by the query object. Backs the on-demand select for a type too large to inline, and is the session-authenticated sibling of the Hire Go search endpoint.
     * @param id
     * @param searchTerm
     * @returns any returns matching values
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeTypesValuesSearch(
        id: string,
        searchTerm?: string,
    ): CancelablePromise<{
        results: Array<{
            label: string;
            value: string;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_types/{id}/values/search',
            path: {
                'id': id,
            },
            query: {
                'search_term': searchTerm,
            },
            errors: {
                404: `attribute type not found`,
            },
        });
    }

    /**
     * Get all attribute types for the account
     * Get attribute types for the current account. When the opening-attributes-v-2-1 feature flag is enabled, each result includes usage counts (funnels_count, locations_count, positions_count) and the list is filtered to types that are either assigned to at least one opening or not assigned to any other record type yet. Pass entity to filter for a different record kind; hiring_goal reads the edit-hiring-goal-customer-attributes flag instead.
     * @param entity Record kind to list types for. Defaults to opening.
     * @returns any returns attribute types for the account
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeTypes(
        entity?: 'opening' | 'hiring_goal',
    ): CancelablePromise<Array<(AttributeType | AttributeTypeWithCounts)>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_types',
            query: {
                'entity': entity,
            },
        });
    }

    /**
     * Create a new attribute type
     * Create a new attribute type for the current account
     * @param requestBody
     * @returns AttributeTypeWithValues created
     * @throws ApiError
     */
    public static postInternalApiCustomerAttributesAttributeTypes(
        requestBody?: {
            type: string;
            value_type: AttributeEntityValueTypes;
            multiselect: boolean;
            attribute_values?: Array<string> | null;
        },
    ): CancelablePromise<AttributeTypeWithValues> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/customer_attributes/attribute_types',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * Update an existing attribute type
     * Update an existing attribute type for the current account
     * @param id Attribute Type ID
     * @param requestBody
     * @returns AttributeTypeWithValues ok
     * @throws ApiError
     */
    public static putInternalApiCustomerAttributesAttributeTypes(
        id: string,
        requestBody?: {
            type: string;
            value_type?: AttributeEntityValueTypes;
            attribute_values?: Array<string>;
        },
    ): CancelablePromise<AttributeTypeWithValues> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/customer_attributes/attribute_types/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * Delete an existing attribute type
     * Soft-deletes an attribute type and all its associated attribute values
     * @param id Attribute Type ID
     * @returns any successful
     * @throws ApiError
     */
    public static deleteInternalApiCustomerAttributesAttributeTypes(
        id: string,
    ): CancelablePromise<{
        success: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/customer_attributes/attribute_types/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `forbidden - missing manage_attributes permission`,
                404: `not found`,
            },
        });
    }

    /**
     * Get usage count for a specific attribute type across all entity types
     * Returns per-entity counts for the delete confirmation modal. Delegates to AttributeTypeCountQuery.counts_for so the numbers are consistent with the listing page.
     * @param id Attribute Type ID
     * @returns AttributeUsageCounts successful
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeTypesUsage(
        id: string,
    ): CancelablePromise<AttributeUsageCounts> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_types/{id}/usage',
            path: {
                'id': id,
            },
            errors: {
                404: `not found`,
            },
        });
    }

    /**
     * Get openings count for a specific attribute type
     * Get the count of openings associated with a specific attribute type
     * @param id Attribute Type ID
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeTypesOpeningsCount(
        id: string,
    ): CancelablePromise<{
        count?: number;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_types/{id}/openings/count',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Get attribute values for given attribute type
     * Get attributes for a given entity
     * @param typeId
     * @returns AttributeValue successful
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeValues(
        typeId?: number,
    ): CancelablePromise<Array<AttributeValue>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_values',
            query: {
                'type_id': typeId,
            },
        });
    }

    /**
     * Get usage counts for a specific attribute value
     * Returns how many openings, positions and hiring goals have this value assigned
     * @param id Attribute Value ID
     * @returns AttributeUsageCounts successful
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesAttributeValuesUsage(
        id: number,
    ): CancelablePromise<AttributeUsageCounts> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/attribute_values/{id}/usage',
            path: {
                'id': id,
            },
            errors: {
                404: `not found`,
            },
        });
    }

    /**
     * Update an attribute value
     * Updates the display value of an existing customer attribute value
     * @param id Attribute Value ID
     * @param requestBody
     * @returns AttributeValue successful
     * @throws ApiError
     */
    public static patchInternalApiCustomerAttributesAttributeValues(
        id: number,
        requestBody: {
            value: string;
        },
    ): CancelablePromise<AttributeValue> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/customer_attributes/attribute_values/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `forbidden - missing manage_attributes permission`,
                404: `not found`,
                422: `unprocessable entity - duplicate value`,
            },
        });
    }

    /**
     * Delete an attribute value
     * Soft-deletes a customer attribute value and removes it from all entities
     * @param id Attribute Value ID
     * @returns any successful
     * @throws ApiError
     */
    public static deleteInternalApiCustomerAttributesAttributeValues(
        id: number,
    ): CancelablePromise<{
        success: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/customer_attributes/attribute_values/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `forbidden - missing manage_attributes permission`,
                404: `not found`,
            },
        });
    }

    /**
     * Gets customer attributes assigned to specified entity
     * Get attributes for a given entity
     * @param entityClass
     * @param id
     * @returns CustomerAttribute successful
     * @throws ApiError
     */
    public static getInternalApiCustomerAttributesEntities(
        entityClass?: string,
        id?: number,
    ): CancelablePromise<Array<CustomerAttribute>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/customer_attributes/entities',
            query: {
                'entity_class': entityClass,
                'id': id,
            },
        });
    }

    /**
     * Updates the attribute values assigned to the entity
     * Update attributes for a given entity
     * @param requestBody
     * @returns CustomerAttribute successful
     * @throws ApiError
     */
    public static putInternalApiCustomerAttributesEntities(
        requestBody?: AttributeEntityParams,
    ): CancelablePromise<Array<CustomerAttribute>> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/customer_attributes/entities',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `forbidden`,
            },
        });
    }

}